import { useEffect, useState } from 'react';
import { Dispatch, connect } from "react-redux";
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { IBaseResponse, IConvertDonationType, IDonorProfileHeaderInfo } from '@nkr/models/index';
import { ICheekSwabModal, IDataChangedWarning, IDonationTypeData, ToggleType, initialDonationTypeDataState } from './models';
import { IApplicationState, AppActionCreators, IAppState, DonorActionCreators, IDonorProfileHeaderInfoResponse, IDonorConversionDataLookupResponse, IDonorConversionPostResponse, DonorSwapActionCreators, IDonorProfileDonorAddressLookupDataResponse, IDonorProfileDonorAddressResponse, ISwapDonorDeleteResponse, ExpertSystemActionCreators, DashCenterActionCreators, IExpertSystemEnrollResponse, ICheckDonorProfileResponse } from '@nkr/store/index';
import { donorProfileSections } from '../subsections';
import { ProfileWrapper, TabWrapper } from './styles';
import DonorDataTab from './donor-data';
import MedicalDetailsTab from './medical-details';
import HlaInfoTab from './hla-info';
import Records from './records';
import Activity from './accepts-declines'
import DonorsNav from '../tabs';
import DonorAddressTab from './donor-address';
import PostTransplant from './post-transplant';
import DonorVouchers from './vouchers';
import VoucherAddEdit from './vouchers/add-edit';
import SwapDonorsNav from '../swap-donors/nav';
import DonorProfileHeaderInfo from './profile-header';
import DonorProfileNav, { getDonorProfileTabMap } from './nav';
import DataChangedWarning from './data-changed-warning';
import CheekSwabRequest, { CheekSwabType } from '@nkr/views/cheek-swab-request-form';
import ConvertDonorType from '../swap-donors/components/convert-donor-type';
import DeleteDonorModal from '../swap-donors/components/delete-donor-modal';
import ExpertSystemEnrollmentConfirm from '../donor-intake/components/expert-system-enrollment-confirm';
import ExpertSystemEnrollmentResponse from '../donor-intake/components/expert-system-enrollment-response';
import { downloadFile } from '@nkr/services/download-file';

interface IProps {
    subSection: string,
    isAdmin: boolean,
    centerId: number,
    donorId: number,
    isMicrositeParticipant: boolean,
    isFollowUpParticipant: boolean,
    isInDonorShield: boolean,
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean,
    groupIsCenter: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDonorHeaderInfo: (centerId, id) => Promise<IDonorProfileHeaderInfoResponse>,
    getProfileDonorAddress: (id: number, centerId: number) => Promise<IDonorProfileDonorAddressResponse>,
    getConversionLookUpData: (donorId: number, centerId: number, isAdmin: boolean) => Promise<IDonorConversionDataLookupResponse>,
    convertDonor: (donorId: number, centerId: number, data: IConvertDonationType, isAdmin: boolean) => Promise<IDonorConversionPostResponse>,
    sendSelfRegistrationInvite: (donorId: number) => Promise<IBaseResponse>,
    doAdminDelete: (donorId: number, reasonCode: number, reason: string, initials: string) => Promise<ISwapDonorDeleteResponse>,
    doCenterDelete: (donorId: number, centerId: number, reasonCode: number, reason: string, initials: string) => Promise<ISwapDonorDeleteResponse>,
    printRequisition: (orderId: number, entityTypeId: number) => Promise<any>,
    enrollExpertSystem: (registrationId: number, bulkType: number, alias: string) => Promise<IExpertSystemEnrollResponse>,
    checkDonorProfile: (registrationId: number) => Promise<ICheckDonorProfileResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const DonorProfile = (props: TProps) => {
    const [headerInfo, setHeaderInfo] = useState<IDonorProfileHeaderInfo>(null);
    const [hasDataChanged, setHasDataChanged] = useState(false);
    const [dataChangedWarning, setDataChangedWarning] = useState<IDataChangedWarning>({ show: false, title: '', href: '' });
    const [toggleType, setToggleType] = useState(ToggleType.NoMessage)
    const [messages, setMessages] = useState<string[]>([]);
    const [showCheekOrder, setShowCheekOrder] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [donationTypeData, setDonationTypeData] = useState<IDonationTypeData>(initialDonationTypeDataState);
    const [cheekSwabModal, setCheekSwabModal] = useState<ICheekSwabModal>({ show: false, type: null });
    const [saveRequested, setSaveRequested] = useState(false);
    const [refreshRequested, setRefreshRequested] = useState(false);
    const [expertSystemEnrollment, setExpertSystemEnrollment] = useState<{ show: boolean, existingDonorAlias: string | null, error: string }>({ show: false, existingDonorAlias: null, error: '' });
    const [expertSystemResponse, setExpertSystemResponse] = useState<{ show: boolean, donorId: number, useBulkKit: number, existingTyping: boolean, existingRequestForTyping: boolean, alias: string }>({ show: false, donorId: 0, useBulkKit: -1, existingTyping: false, existingRequestForTyping: false, alias: '' });

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.getDonorHeaderInfo(props.centerId, props.donorId).then(response => {
            if (response.data.success) {
                setHeaderInfo(response.data.results);
            }
        });
    };

    const handleTabClick = (href: string) => {
        if (hasDataChanged) {
            let t = getDonorProfileTabMap(headerInfo.isAdp, headerInfo.isTransplanted).find(x => href.indexOf(x.href) >= 0);
            let title = t != null ? t.title : '';

            setDataChangedWarning({
                show: true,
                title,
                href
            });
        } else {
            window.location.href = href;
        }
    };

    const handleDiscardChanges = () => {
        let href = dataChangedWarning.href;

        setDataChangedWarning({
            show: false,
            title: '',
            href: ''
        });

        window.location.href = href;
    };

    const handleCancelWarning = () => {
        setDataChangedWarning({
            show: false,
            title: '',
            href: ''
        });
    };

    const handleRefreshHeader = () => {
        getData();
    };

    const handleDataChanged = (hasDataChanged: boolean) => {
        setHasDataChanged(hasDataChanged);
    };

    const handleUpdateForErrors = (toggleType: ToggleType, messages: string[]) => {
        setToggleType(toggleType);
        setMessages(messages);
    };

    const handleGetCheekOrderData = (type: CheekSwabType) => {
        let { donorId, centerId } = props;

        props.showLoading('Getting address data, one moment please...').then(() => {
            Promise.all([
                props.getProfileDonorAddress(donorId, centerId),
            ])
                .then(([addressDataResponse]) => {
                    if (addressDataResponse.data.success) {
                        setCheekSwabModal({ show: true, type });
                    }
                })
                .finally(() => {
                    props.hideLoading()
                });
        });
    };

    const handleCancelCheekSwabOrder = (cancel: boolean) => {
        setCheekSwabModal({
            show: false,
            type: null
        });

        if (!cancel) {
            handleRefreshHeader();
        }
    };

    const handleSendSelfRegistrationInvite = () => {
        props.sendSelfRegistrationInvite(props.donorId).then(response => {
            if (response.data.success) {
                toast.success('Voucher Self Registration Email Succesfully sent');
            } else {
                toast.error('Voucher Self Registration Email was unable to be processed');
            }
        });
    };

    const handleShowDonorConversion = () => {
        let { centerId, donorId } = props;

        props.showLoading('Retrieving conversion data, please wait...').then(() => {
            props.getConversionLookUpData(donorId, centerId, props.isAdmin)
                .then(response => {
                    if (response.data.success) {
                        let { isPaired, hasIntended, isExpertSystem, hasDashReferral, typeList } = response.data.results;

                        setDonationTypeData({
                            show: true,
                            donorId,
                            isPaired,
                            hasIntended,
                            isExpertSystem,
                            hasDashReferral,
                            typeList
                        });
                    } else {
                        toast.error('Donation type data was unable to load...');
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error('Donation type data was unable to load...');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleSaveDonorConversion = (data: IConvertDonationType, donorId: number) => {
        let { centerId } = props;

        props.showLoading('Converting Donation Type, please wait...').then(() => {
            props.convertDonor(donorId, centerId, data, props.groupIsSuper)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Donation type successfully updated...');
                        setDonationTypeData(initialDonationTypeDataState);
                        setRefreshRequested(true);
                    } else {
                        toast.error(response.data.message);
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error('Donation type was unable to be converted...');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleHideDonorConversion = () => {
        setDonationTypeData(initialDonationTypeDataState);
    };

    const handleShowDeleteDonorModal = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteDonor = (reasonCode: number, reason: string, initials: string) => {
        let { centerId, donorId, isAdmin } = props;

        let doDelete = isAdmin ? props.doAdminDelete(donorId, reasonCode, reason, initials) : props.doCenterDelete(donorId, centerId, reasonCode, reason, initials);

        props.showLoading('Deleting donor, one moment please...').then(() => {
            doDelete
                .then(res => {
                    if (res.data.success) {
                        toast.success('Donor succesfully deleted...')

                        handleHideDeleteDonor(true);

                        window.location.href = `/center/${centerId}/donors/swap`;
                    }
                })
                .catch((err: AxiosError) => {
                    toast.error('Donor was unable to be deleted...');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handlePrintRequisition = (requsitionId: number, entityTypeId: number) => {
        props.showLoading('Downloading requisition file, one moment please...').then(() => {
            props.printRequisition(requsitionId, entityTypeId).then((response: any) => {
                downloadFile(response.data, `requisition-file.pdf`, 'application/pdf');

                props.hideLoading();
            });
        });
    }

    const handleHideDeleteDonor = (reload: boolean = false) => {
        setShowDeleteModal(false);
    };

    const handleSave = () => {
        setSaveRequested(true);
    };

    const handleSaveFinished = (refreshHeader: boolean) => {
        setSaveRequested(false);

        if (refreshHeader) {
            getData();
        }
    };

    const handleRefreshFinished = () => {
        setRefreshRequested(false);
    };

    const handleResetSaveRequest = () => {
        setSaveRequested(false);
    }

    const handleShowStartExpertSystem = () => {
        props.showLoading('Checking donor profile...').then(() => {
            props.checkDonorProfile(headerInfo.registrationId)
                .then(response => {
                    const existingDonorAlias = response.data.success && response.data.results.exists
                        ? response.data.results.alias
                        : null;
                    setExpertSystemEnrollment({ show: true, existingDonorAlias, error: '' });
                })
                .finally(() => { props.hideLoading(); });
        });
    };

    const handleProcessStartExpertSystem = (useBulkKit: number, alias: string) => {
        props.showLoading('Enrolling donor in Expert System...').then(() => {
            props.enrollExpertSystem(headerInfo.registrationId, useBulkKit, alias)
                .then(response => {
                    if (response.data.success) {
                        const { donorId, existingTyping, existingRequestForTyping } = response.data.results;
                        setExpertSystemEnrollment({ show: false, existingDonorAlias: null, error: '' });
                        setExpertSystemResponse({ show: true, donorId, useBulkKit, existingTyping, existingRequestForTyping, alias });
                        getData();
                    } else {
                        setExpertSystemEnrollment(prev => ({ ...prev, error: response.data.message }));
                    }
                })
                .finally(() => { props.hideLoading(); });
        });
    };

    const handleCancelStartExpertSystem = () => {
        setExpertSystemEnrollment({ show: false, existingDonorAlias: null, error: '' });
    };

    const handleCancelExpertSystemResponse = () => {
        setExpertSystemResponse({ show: false, donorId: 0, useBulkKit: -1, existingTyping: false, existingRequestForTyping: false, alias: '' });
    };

    const alias = headerInfo != null ? headerInfo.alias : '';
    const isAdp = headerInfo != null ? headerInfo.isAdp : false;
    const recipientId = headerInfo != null ? headerInfo.recipientId : 0;
    const recipientAlias = headerInfo != null ? headerInfo.recipientAlias : '';
    const recipientCenterId = headerInfo != null ? headerInfo.recipientCenterId : 0;
    const recipientType = headerInfo != null ? headerInfo.recipientType : null;

    return (
        <>
            <div style={{ padding: '0px 10px' }}>
                <DonorsNav
                    activeKey={'PROFILE'}
                    centerId={props.centerId}
                    isMicrositeParticipant={props.isMicrositeParticipant}
                    isAdmin={props.isAdmin}
                    isFollowUpParticipant={props.isFollowUpParticipant}
                    isInDonorShield={props.isInDonorShield}
                    organType={props.app.site.organType}
                    onTabSelect={() => null}
                />

                <TabWrapper>
                    <ProfileWrapper>
                        <SwapDonorsNav
                            activeKey={'EDIT'}
                            centerId={props.centerId}
                            donorId={props.donorId}
                            donorAlias={alias}
                            donorIsAdp={isAdp}
                            recipientId={recipientId}
                            recipientAlias={recipientAlias}
                            recipientCenterId={recipientCenterId}
                            recipientType={recipientType}
                            isAdminLogin={props.groupIsSuper}
                            onTabChange={function (tab: string): void {
                                throw new Error('Function not implemented.');
                            }}
                        />

                        <DonorProfileHeaderInfo
                            data={headerInfo}
                            centerId={props.centerId}
                            toggleType={toggleType}
                            messages={messages}
                            activeKey={props.subSection}
                            cheekSwabNotification={showCheekOrder}
                            cheekOrderDataNotify={() => null}
                            hideCheekOrderDataNotify={() => null}
                            onTabChange={handleTabClick}
                        />

                        <DonorProfileNav
                            data={headerInfo}
                            activeKey={props.subSection}
                            isAdmin={props.isAdmin}
                            isSuper={props.groupIsSuper}
                            isTransplanted={headerInfo != null ? headerInfo.isTransplanted : false}
                            canOrderCheekSwab={headerInfo != null ? headerInfo.canOrderCheekSwab : false}
                            centerId={props.centerId}
                            hasChanges={hasDataChanged}
                            onTabClick={handleTabClick}
                            onSave={handleSave}
                            onGetCheekOrderData={() => handleGetCheekOrderData(CheekSwabType.Order3gTissueTyping)}
                            onUtilizeExistingCheekSwab={() => handleGetCheekOrderData(CheekSwabType.UtilizeBulkCheekSwab)}
                            onShowDeleteModal={handleShowDeleteDonorModal}
                            onSendSelfRegistrationInvite={handleSendSelfRegistrationInvite}
                            onShowDonorConversion={handleShowDonorConversion}
                            onPrintRequisition={handlePrintRequisition}
                            onStartExpertSystem={handleShowStartExpertSystem}
                        />

                        {props.subSection == donorProfileSections.DONOR_DATA && (
                            <DonorDataTab
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.DONOR_ADDRESS && (
                            <DonorAddressTab
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.MEDICAL_DETAILS && (
                            <MedicalDetailsTab
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                                onResetSaveRequest={handleResetSaveRequest}
                            />
                        )}

                        {props.subSection == donorProfileSections.HLA_INFO && (
                            <HlaInfoTab
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.RECORDS && (
                            <Records
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo} hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.ACTIVITY && (
                            <Activity
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.POST_TRANSPLANT && (
                            <PostTransplant
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {props.subSection == donorProfileSections.VOUCHERS && (
                            <DonorVouchers
                                centerId={props.centerId}
                                isAdmin={props.isAdmin}
                                isAdminLogin={props.app.user.group.isSuper}
                                donorId={props.donorId}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}

                        {[donorProfileSections.VOUCHER_ADD, donorProfileSections.VOUCHER_EDIT].indexOf(props.subSection) >= 0 && (
                            <VoucherAddEdit
                                centerId={props.centerId}
                                activeKey={props.subSection}
                                isAdmin={props.isAdmin}
                                isAdminLogin={props.app.user.group.isSuper}
                                donorId={props.donorId}
                                isMicrositeParticipant={props.isMicrositeParticipant}
                                isFollowUpParticipant={props.isFollowUpParticipant}
                                isInDonorShield={props.isInDonorShield}
                                headerInfo={headerInfo}
                                hasDataChanged={hasDataChanged}
                                saveRequested={saveRequested}
                                refreshRequested={refreshRequested}
                                onDataChanged={handleDataChanged}
                                onSaveFinished={handleSaveFinished}
                                onRefreshFinished={handleRefreshFinished}
                                onResetDataChangedWarning={handleCancelWarning}
                                onTabClick={handleTabClick}
                                onUpdateForErrors={handleUpdateForErrors}
                            />
                        )}
                    </ProfileWrapper>
                </TabWrapper>
            </div>

            <DataChangedWarning
                show={dataChangedWarning.show}
                title={dataChangedWarning.title}
                onDiscard={handleDiscardChanges}
                onCancel={handleCancelWarning}
            />

            <CheekSwabRequest
                centerId={props.centerId}
                donorId={props.donorId}
                recipientId={0}
                isModal={true}
                show={cheekSwabModal.show}
                type={cheekSwabModal.type}
                onFinished={handleCancelCheekSwabOrder} />

            <DeleteDonorModal
                show={showDeleteModal}
                alias={headerInfo != null ? headerInfo.alias : ''}
                onDelete={handleDeleteDonor}
                onCancel={handleHideDeleteDonor}
            />

            <ConvertDonorType
                show={donationTypeData.show}
                donorId={donationTypeData.donorId}
                isPaired={donationTypeData.isPaired}
                hasIntended={donationTypeData.hasIntended}
                isExpertSystem={donationTypeData.isExpertSystem}
                hasDashReferral={donationTypeData.hasDashReferral}
                isAdmin={props.groupIsSuper}
                typeList={donationTypeData.typeList}
                onSave={handleSaveDonorConversion}
                onCancel={handleHideDonorConversion} />

            <ExpertSystemEnrollmentConfirm
                show={expertSystemEnrollment.show}
                data={headerInfo != null ? {
                    firstName: headerInfo.alias,
                    lastName: '',
                    isExpertSystem: false,
                    isInExpertSystem: headerInfo.isInExpertSystem,
                    hasHlaAlleleTyping: headerInfo.alleleData ?? false
                } : null}
                error={expertSystemEnrollment.error}
                existingDonorAlias={expertSystemEnrollment.existingDonorAlias}
                onConfirm={handleProcessStartExpertSystem}
                onCancel={handleCancelStartExpertSystem}
            />

            <ExpertSystemEnrollmentResponse
                show={expertSystemResponse.show}
                donorId={expertSystemResponse.donorId}
                centerId={props.centerId}
                useBulkKit={expertSystemResponse.useBulkKit}
                existingTyping={expertSystemResponse.existingTyping}
                existingRequestForTyping={expertSystemResponse.existingRequestForTyping}
                alias={expertSystemResponse.alias}
                onCancel={handleCancelExpertSystemResponse}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper,
        groupIsCenter: appState.app.user.group.isCenter
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getDonorHeaderInfo: (centerId, id) => {
            return dispatch(DonorActionCreators.getDonorHeaderInfo(centerId, id));
        },
        getProfileDonorAddress: (id, centerId) => {
            return dispatch(DonorActionCreators.getProfileDonorAddress(id, centerId));
        },
        // getProfileDonorAddressLookupData: (centerId) => {
        //     return dispatch(DonorActionCreators.getProfileDonorAddressLookupData(centerId));
        // },
        getConversionLookUpData: (donorId, centerId, isAdmin) => {
            return dispatch(DonorSwapActionCreators.getConversionLookUpData(donorId, centerId, isAdmin))
        },
        convertDonor: (donorId, centerId, data, isAdmin) => {
            return dispatch(DonorSwapActionCreators.convertDonor(donorId, centerId, data, isAdmin));
        },
        sendSelfRegistrationInvite: (donorId) => {
            return dispatch(DonorSwapActionCreators.sendSelfRegistrationInvite(donorId))
        },
        doAdminDelete: (donorId, reasonCode, reason, initials) => {
            return dispatch(DonorSwapActionCreators.doAdminDelete(donorId, reasonCode, reason, initials))
        },
        doCenterDelete: (donorId, centerId, reasonCode, reason, initials) => {
            return dispatch(DonorSwapActionCreators.doCenterDelete(donorId, centerId, reasonCode, reason, initials))
        },
        printRequisition: (orderId, entityType) => {
            return dispatch(ExpertSystemActionCreators.printRequisition(orderId, entityType));
        },
        enrollExpertSystem: (registrationId, bulkType, alias) => {
            return dispatch(DashCenterActionCreators.enrollExpertSystem(registrationId, bulkType, alias));
        },
        checkDonorProfile: (registrationId) => {
            return dispatch(DashCenterActionCreators.checkDonorProfile(registrationId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(DonorProfile);
